/* eslint-disable no-console */
/**
 * Structured logger with level filtering.
 */

export const enum LogLevel {
  Debug = 0,
  Info = 1,
  Warn = 2,
  Error = 3,
}

let currentLevel: LogLevel = LogLevel.Info;

/**
 * A log sink receives every line that passes the level filter (after it is
 * written to the console). `level` is the numeric LogLevel (0 debug … 3 error)
 * — a plain number so consumers outside the const-enum inlining boundary
 * (apps bundling the engine from source) can subscribe without importing it.
 */
export type LogSink = (level: number, tag: string, msg: string, args: unknown[]) => void;
const sinks = new Set<LogSink>();
function emit(level: number, tag: string, msg: string, args: unknown[]): void {
  for (const s of sinks) {
    try { s(level, tag, msg, args); } catch { /* a faulty sink never breaks logging */ }
  }
}

export const Logger = {
  setLevel(level: LogLevel): void {
    currentLevel = level;
  },

  /**
   * Subscribe to emitted log lines (e.g. an in-app console that mirrors the
   * scripting runtime's `[Lua]` errors). Returns the unsubscribe function.
   */
  onLog(sink: LogSink): () => void {
    sinks.add(sink);
    return () => { sinks.delete(sink); };
  },

  debug(tag: string, msg: string, ...args: unknown[]): void {
    if (currentLevel <= LogLevel.Debug) {
      console.debug(`[${tag}]`, msg, ...args);
      emit(LogLevel.Debug, tag, msg, args);
    }
  },

  info(tag: string, msg: string, ...args: unknown[]): void {
    if (currentLevel <= LogLevel.Info) {
      console.info(`[${tag}]`, msg, ...args);
      emit(LogLevel.Info, tag, msg, args);
    }
  },

  warn(tag: string, msg: string, ...args: unknown[]): void {
    if (currentLevel <= LogLevel.Warn) {
      console.warn(`[${tag}]`, msg, ...args);
      emit(LogLevel.Warn, tag, msg, args);
    }
  },

  error(tag: string, msg: string, ...args: unknown[]): void {
    if (currentLevel <= LogLevel.Error) {
      console.error(`[${tag}]`, msg, ...args);
      emit(LogLevel.Error, tag, msg, args);
    }
  },
};
